import { useCallback, useEffect, useRef } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { App } from "../app/App";
import { AuthPage } from "../app/auth/AuthPage";
import { Registration } from "../app/auth/components/Registration";
import { Upload } from "../app/compliance/Partner/Manage/Upload";
import { ErrorsPage } from "../app/errors/ErrorsPage";
import { PrivacyPolicyPage } from "../app/legal/PrivacyPolicyPage";
import { ShopifySignupPage } from "../app/integrationV1/shopify/welcome/ShopifySignupPage";
import { ShopifySignupVerifyPage } from "../app/integrationV1/shopify/welcome/ShopifySignupVerifyPage";
import { ShopifyWelcomePage } from "../app/integrationV1/shopify/welcome/ShopifyWelcomePage";
import { apiSlice } from "../store/apiSlice/apiSlice";
import { authApiSlice, useAutoLoginQuery } from "../store/apiSlice/authApiSlice";
import { isAuthenticated, logOut } from "../store/slice/authSlice";
import { store } from "../store/store";
import { HomeRedirect } from "./HomeRedirect";
import { PrivateRoutes } from "./PrivateRoutes";
import { PrivateRoutes2 } from "./PrivateRoutes2";
import { PublicQuotation } from "../app/quotation/public/PublicQuotation";

const AppRoutes = () => {
  const isAuth = useSelector(isAuthenticated);
  const { isLoading, isFetching } = useAutoLoginQuery(undefined, {
    // Only bootstrap once while auth is unknown; skip after login/logout so
    // resetApiState() on logout does not trigger another autologin request.
    skip: isAuth !== null,
  });
  const pendingAuthIn = useRef(false);

  const dispatch = useDispatch();

  const syncAutologinFromPeer = useCallback(() => {
    dispatch(authApiSlice.endpoints.autoLogin.initiate(undefined, { forceRefetch: true }));
  }, [dispatch]);

  useEffect(() => {
    const handleStorageChange = event => {
      if (event.key === "authOut" && event.newValue !== null && event.oldValue !== event.newValue) {
        const wasAuthenticated = isAuthenticated(store.getState());
        dispatch(logOut());
        if (wasAuthenticated) {
          dispatch(apiSlice.util.resetApiState());
        }
      } else if (
        event.key === "authIn" &&
        event.newValue !== null &&
        event.oldValue !== event.newValue
      ) {
        if (!isAuth) {
          if (!isFetching && !isLoading) {
            syncAutologinFromPeer();
          } else {
            pendingAuthIn.current = true;
          }
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => window.removeEventListener("storage", handleStorageChange);
  }, [dispatch, isAuth, isFetching, isLoading, syncAutologinFromPeer]);

  useEffect(() => {
    if (pendingAuthIn.current && !isAuth && !isFetching && !isLoading) {
      pendingAuthIn.current = false;
      syncAutologinFromPeer();
    }
  }, [isAuth, isFetching, isLoading, syncAutologinFromPeer]);

  if ([true, false].includes(isAuth)) {
    return (
      <BrowserRouter>
        <Routes>
          <Route element={<App />}>
            <Route path="/join/:token" element={<Registration />} />
            <Route path="/shopify/welcome" element={<ShopifyWelcomePage />} />
            <Route path="/shopify/signup" element={<ShopifySignupPage />} />
            <Route path="/signup/verify" element={<ShopifySignupVerifyPage />} />
            <Route path="/upload/:token" element={<Upload />} />
            <Route path="/quotations/:token/public" element={<PublicQuotation />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="error/*" element={<ErrorsPage />} />

            {isAuth ? (
              <>
                <Route path="/new/*" element={<PrivateRoutes2 />} />
                <Route path="/*" element={<PrivateRoutes />} />
                <Route index element={<HomeRedirect />} />
              </>
            ) : (
              <>
                <Route path="/*" element={<AuthPage />} />
                <Route index element={<Navigate to="/login" />} />
              </>
            )}
          </Route>
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <div className="w-100 vh-100 d-flex justify-content-center align-items-center">
      {["primary", "secondary", "success", "danger"].map((item, index) => (
        <Spinner key={`${index}_${item}`} animation="grow" variant={item} />
      ))}
    </div>
  );
};

export { AppRoutes };
